import { useMemo } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import messages from './messages'
import { useMutateAttachInvoiceToItems } from '../../models'

export interface IUseAttachInvoiceToMatchedItemsPresenterParams {
  /** Item source (from feature 21 via the FS) — resolves existing slips */
  matches: ReconcileMatch[]
}

// Companion hook for the FS-gated confirm (ADR-2). `attach` appends the
// invoice UUID to each selected item's existing packing_slips (BHV-048).
export const useAttachInvoiceToMatchedItemsPresenter = ({
  matches,
}: IUseAttachInvoiceToMatchedItemsPresenterParams) => {
  const intl = useProcurifyIntl()
  const { attach, isAttaching } = useMutateAttachInvoiceToItems()

  const itemsById = useMemo(() => {
    const map = new Map<number, CreditCardItem>()
    matches.forEach((match) =>
      match.creditcardItems.forEach((item) => map.set(item.id, item))
    )
    return map
  }, [matches])

  const attachInvoice = async (fileUuid: string, itemIds: number[]) => {
    const items = itemIds
      .map((id) => itemsById.get(id))
      .filter((item): item is CreditCardItem => item !== undefined)
      .map((item) => ({
        id: item.id,
        packingSlipUuids: [
          ...item.packingSlips.map((slip) => slip.uuid),
          fileUuid,
        ],
      }))

    try {
      await attach({ items })

      toast.success({
        title: intl.formatMessage(messages.successTitle),
        children: intl.formatMessage(messages.successMessage),
      })
    } catch (error) {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.genericErrorMessage),
      })

      throw error
    }
  }

  return {
    commands: {
      attach: attachInvoice,
    },
    states: {
      isAttaching,
    },
  }
}
